import { useQuery } from '@tanstack/react-query';
import { ComponentProps } from 'react';

import Card from '~/shared/components/Card';
import { useXFetch } from '~/shared/utils/useXFetch';

import UtilizationChart from '../common/UtilizationChart';

import SavingsSection from './SavingsSection';

type SavingStatsResponse = {
  yearlyEstimatedSavings: number;
  yearlyPotentialSavings: number;
  capacityCoverage: number;
  overallCapacity: number;
};

function EmptyState() {
  return (
    <div className="w-full h-full flex justify-center items-center text-[12px]">No Results</div>
  );
}

function SavingCharts({ queryUrl }: { queryUrl: string }) {
  const api = useXFetch();
  const { data, isLoading, isRefetching, error } = useQuery({
    queryKey: [queryUrl],
    queryFn: () => api<SavingStatsResponse>(queryUrl),
    refetchOnWindowFocus: false,
  });

  const { yearlyEstimatedSavings, yearlyPotentialSavings, capacityCoverage, overallCapacity } =
    data || {};

  if (error || (!data && !isLoading && !isRefetching)) {
    return <EmptyState />;
  }

  return (
    <div className="flex flex-col gap-[51px] justify-center">
      <div className="text-[18px] h-[48px] flex items-center leading-[24px] font-medium w-fit">
        Savings Potential
      </div>

      <div className="flex h-full gap-8 justify-between px-2">
        <SavingsSection
          variant="purple"
          savingsText={`$${yearlyEstimatedSavings}`}
          potentialText={`$${yearlyPotentialSavings} full potential`}
          percentage={
            yearlyPotentialSavings && yearlyEstimatedSavings
              ? Math.round((yearlyEstimatedSavings / yearlyPotentialSavings) * 100)
              : 0
          }
          title="Yearly Saving using Datafy"
          potentialLegendText="Full Potential"
          savingLegendText="Estimated"
          loading={isLoading || isRefetching}
        />
        <SavingsSection
          variant="green"
          savingsText={`${capacityCoverage}GiB`}
          potentialText={`${overallCapacity}GiB`}
          percentage={
            capacityCoverage && overallCapacity
              ? Math.round((capacityCoverage / overallCapacity) * 100)
              : 0
          }
          title="EBS Covered by Datafy"
          potentialLegendText="Overall Capacity"
          savingLegendText="Datafy Coverage"
          loading={isLoading || isRefetching}
        />
      </div>
    </div>
  );
}

export default function DashboardCharts({
  savingsQueryUrl,
  utilizationQueryUrl,
  activeDateFilter,
  activeRefreshState,
  onSelectDateFilter,
  onSelectRefreshState,
  onClickRefresh,
}: {
  savingsQueryUrl: string;
  utilizationQueryUrl: string;
} & Omit<ComponentProps<typeof UtilizationChart>, 'queryUrlString'>) {
  return (
    <div className="w-full grid grid-cols-2 gap-[24px] mb-[24px] h-[444px]">
      <Card className="flex flex-col">
        <SavingCharts queryUrl={savingsQueryUrl} />
      </Card>

      <UtilizationChart
        activeDateFilter={activeDateFilter || 'Last 3 hours'}
        activeRefreshState={activeRefreshState || ''}
        onSelectRefreshState={onSelectRefreshState}
        onSelectDateFilter={onSelectDateFilter}
        queryUrlString={utilizationQueryUrl}
        onClickRefresh={onClickRefresh}
      />
    </div>
  );
}
